import React, { useEffect, useState } from 'react'

import * as S from './styles'
import Heading from 'presentation/shared/components/Heading'
import Checkbox from 'presentation/shared/components/Checkbox'
import Modal from 'presentation/hospital/components/Modal'
import Button from 'presentation/shared/components/Button'
import { useFormik } from 'formik'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import { LoadSurgicalOrder } from 'domain/usecases/surgical-order/load-surgical-order'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import { getDocumentsLabelLocationByEnum } from 'presentation/utils/document-types-location'
import { getSurgicalOrderGuideDocumentType } from 'presentation/utils/get-surgical-order-guide-document-type'
import { toast } from 'react-toastify'
import {
  WithLoading,
  WithLoadingProps
} from '../../../shared/components/HOCs/WithLoading'
import { GuidesUnvaibleModal } from './GuidesUnvaibleModal'
import { useServices } from 'presentation/hooks/use-services'
import { DoctorDocument } from 'common/enum/doctor-document'

export type Props = {
  close: () => void
  surgicalOrderId: number
  loadSurgicalOrder?: LoadSurgicalOrder
  loadSurgicalOrderDocument?: LoadSurgicalOrderDocument
  somethingWrong?: boolean
  guidesUnvaible?: boolean
} & WithLoadingProps

interface FormValues {
  'opme-guide': boolean
  'sp-sadt-guide': boolean
  'hospitalization-request-guide': boolean
  'surgical-planning-guide': boolean
  'surgical-order-guide': boolean
}

interface FriendlyNames {
  [key: string]: string
}

export const GuidesModal = WithLoading(
  ({
    close,
    surgicalOrderId,
    loadSurgicalOrder,
    loadSurgicalOrderDocument,
    setIsLoading
  }: Props) => {
    const surgicalOrderService = useServices().surgicalOrder
    const [surgicalOrder, setSurgicalOrder] = useState<SurgicalOrderModel>()
    const [checkedGuides, setCheckedGuides] = useState<any>()
    const [somethingWrong, setSomethingWrong] = useState<boolean>(false)

    const checkGuides = async () => {
      try {
        const response = await surgicalOrderService.checkGuidesGenerated({
          surgical_order_id: surgicalOrderId
        })

        setCheckedGuides(response)
      } catch (error: any) {
        toast.error('Ocorreu um erro ao verificar guias!')
      }
    }

    const hasFalseValue = (guides: Record<string, boolean>): boolean => {
      return Object.values(guides).includes(false)
    }

    const guidesNotGenrated = checkedGuides && hasFalseValue(checkedGuides)

    const handleGenerateGuides = async () => {
      try {
        setIsLoading(true)

        const regenarateGuides: string[] = []

        const friendlyNames: FriendlyNames = {
          'opme-guide': DoctorDocument.GUIDE_OPME,
          'sp-sadt-guide': DoctorDocument.GUIDE_SPSADT,
          'hospitalization-request-guide':
            DoctorDocument.GUIDE_HOSPITALIZATION_REQUEST,
          'surgical-planning-guide': DoctorDocument.GUIDE_SURGICAL_PLANNING
        }

        for (const key in formik.values) {
          if (formik.values[key as keyof FormValues]) {
            regenarateGuides.push(friendlyNames[key as keyof FormValues])
          }
        }

        const regenerateGuidesResponse =
          await surgicalOrderService.regenerateGuides({
            surgical_order_id: surgicalOrderId,
            types: regenarateGuides
          })

        await loadData()
        await checkGuides()
        await formik.submitForm()
        return regenerateGuidesResponse
      } catch (error) {
        setSomethingWrong(true)
      } finally {
        setIsLoading(false)
      }
    }

    async function loadData() {
      try {
        setIsLoading(true)
        setSurgicalOrder(
          await loadSurgicalOrder?.load(surgicalOrderId, [
            'documents {document_id, type, name}',
            'hospitalizationMode'
          ])
        )
      } catch (e: any) {
        toast.error(e.message)
      } finally {
        setIsLoading(false)
      }
    }

    const formik = useFormik({
      initialValues: {
        'hospitalization-request-guide': false,
        'surgical-planning-guide': false,
        'opme-guide': false,
        'surgical-order-guide': false,
        'sp-sadt-guide': false
      },
      onSubmit: async (values) => {
        try {
          setIsLoading(true)
          const guides = Object.entries(values).filter((val) => val[1])
          for (const guide of guides) {
            const guideType = getSurgicalOrderGuideDocumentType(guide[0])
            const document_id = Number(
              surgicalOrder?.documents?.find(
                (document) =>
                  document.type.toUpperCase() === guideType.toUpperCase()
              )?.document_id
            )
            const file = await loadSurgicalOrderDocument?.load(
              surgicalOrderId,
              document_id
            )
            if (file) {
              downloadFileFromBlob(
                file?.data,
                file.contentType,
                getDocumentsLabelLocationByEnum(guideType)
              )
            }
          }
          toast.success('As guias foram geradas com sucesso!')
          formik.resetForm()
        } catch (error: any) {
          toast.error('Ocorreu um erro ao gerar guias!')
        } finally {
          setIsLoading(false)
        }
      }
    })

    useEffect(() => {
      loadData()
      checkGuides()
    }, [])

    return (
      <>
        {!somethingWrong && (
          <Modal close={close}>
            <S.Wrapper onSubmit={formik.handleSubmit}>
              {guidesNotGenrated === false && (
                <Heading size="xlarge" as="h1" style={{ marginBottom: '85px' }}>
                  Gerar guias
                </Heading>
              )}
              {guidesNotGenrated === true && (
                <S.SomethingWrongContainer>
                  <h1>Algo deu errado!</h1>
                  <span>
                    Ocorreu um erro em uma ou mais guias listadas abaixo não
                    puderam ser geradas . Clique em regerar guias.
                  </span>
                </S.SomethingWrongContainer>
              )}
              <S.CheckboxLayout>
                {!checkedGuides?.[DoctorDocument.GUIDE_OPME] && (
                  <Checkbox
                    label="Guia de OPME"
                    labelFor="opme"
                    id="opme"
                    name="opme-guide"
                    onChange={formik.handleChange}
                    checked={formik.values['opme-guide']}
                  />
                )}
                {!checkedGuides?.[DoctorDocument.GUIDE_SPSADT] && (
                  <Checkbox
                    label="Guia SADT"
                    labelFor="sadt"
                    id="sadt"
                    name="sp-sadt-guide"
                    onChange={formik.handleChange}
                    checked={formik.values['sp-sadt-guide']}
                  />
                )}
                {!checkedGuides?.[
                  DoctorDocument.GUIDE_HOSPITALIZATION_REQUEST
                ] && (
                  <Checkbox
                    label="Guia solicitação de internação"
                    labelFor="hospitalization-request-guide"
                    id="hospitalization-request-guide"
                    name="hospitalization-request-guide"
                    onChange={formik.handleChange}
                    checked={formik.values['hospitalization-request-guide']}
                  />
                )}
                {!checkedGuides?.[DoctorDocument.GUIDE_SURGICAL_PLANNING] && (
                  <Checkbox
                    label="Planejamento cirúrgico"
                    labelFor="surgicalPlanning"
                    id="surgicalPlanning"
                    name="surgical-planning-guide"
                    onChange={formik.handleChange}
                    checked={formik.values['surgical-planning-guide']}
                  />
                )}

                {guidesNotGenrated === false && (
                  <>
                    <Checkbox
                      label="Guia solicitação de internação"
                      labelFor="hospitalization-request-guide"
                      id="hospitalization-request-guide"
                      name="hospitalization-request-guide"
                      onChange={formik.handleChange}
                      checked={formik.values['hospitalization-request-guide']}
                    />
                    <Checkbox
                      label="Planejamento cirúrgico"
                      labelFor="surgicalPlanning"
                      id="surgicalPlanning"
                      name="surgical-planning-guide"
                      onChange={formik.handleChange}
                      checked={formik.values['surgical-planning-guide']}
                    />
                    <Checkbox
                      label="Guia de OPME"
                      labelFor="opme"
                      id="opme"
                      name="opme-guide"
                      onChange={formik.handleChange}
                      checked={formik.values['opme-guide']}
                    />
                    <Checkbox
                      label="Guia SADT"
                      labelFor="sadt"
                      id="sadt"
                      name="sp-sadt-guide"
                      onChange={formik.handleChange}
                      checked={formik.values['sp-sadt-guide']}
                    />
                  </>
                )}
              </S.CheckboxLayout>
              <S.Buttons>
                <Button variant="outlined" type="button" onClick={close}>
                  Cancelar
                </Button>
                {guidesNotGenrated === false ? (
                  <Button
                    type="submit"
                    disabled={
                      !Object.values(formik.values).some((val) => val) ||
                      formik.isSubmitting
                    }
                  >
                    Gerar guias
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleGenerateGuides}
                    disabled={
                      !Object.values(formik.values).some((val) => val) ||
                      formik.isSubmitting
                    }
                  >
                    Regerar guias
                  </Button>
                )}
              </S.Buttons>
            </S.Wrapper>
          </Modal>
        )}
        {somethingWrong && <GuidesUnvaibleModal close={close} />}
      </>
    )
  }
)
